"use client";

import { API_ENDPOINT } from "@/constants/appConstants";
import { getRuntimeVariables, getSiteProps } from "@/utils";
import { getAbModuleId, resolveABVariant } from "@/utils/abModule";
import cleverTapAndMixPanelEvents from "@/utils/eventTracking";
import { Header } from "@monsterindia/global-ui-components";
import "@monsterindia/global-ui-components/dist/styles.css";
import { FC, memo, useEffect, useState } from "react";

import {
  accountList,
  cartData,
  companyList,
  hamburgerBanner,
  iconDataForGuest,
  mobileBottomNav,
  userIconsData,
} from "../constant";
import { fetchHeaderNavLinks, fetchTopLocations } from "../query";
import {
  FormattedData,
  HeaderLinksProps,
  HeaderPropsType,
  SearchDynamicData,
} from "../types";
import {
  SHOW_TOP_LOCATION,
  brandLogo,
  brandLogoName,
  callAutoFillAPI,
  fetchDataToBePrefilled,
  generateExperienceYear,
  handlerLoginRegister,
  processSearchedData,
  recentSearchSelectHandler,
} from "../utils";

const CustomHeader: FC<HeaderPropsType> = (props) => {
  const { host, isLoggedIn, isMobile, isApp, authUserData, hamburgerData } =
    props;
  const { notificationCount, cartCount, kiwiUserId } = authUserData;
  const lottieUrl = window?._siteProps_?.propertiesMap?.logoUrl;
  const [navLinks, setNavLinks] = useState<HeaderLinksProps[]>([]);
  const [location, setLocation] = useState<FormattedData[]>([]);
  const [searchData, setSearchData] = useState<SearchDynamicData>({
    recentSearches: [],
    modifier: "",
    prefilledDataforLocation: "",
  });
  const [showNewSEOPage, setShowNewSEOPage] = useState(false);

  const isProd = getRuntimeVariables(`${host}`);
  const siteProps = getSiteProps(host);

  const mediaUrl = siteProps?.media_url || "";
  const site_map = siteProps?.site_map || "";
  const category = "react-msite";
  const cart = isProd === "prod" ? "/cart" : "/web/en/cart";
  const showHeaderInMobileForPathNames = ["Dashboard"];
  const showSearchBarBelowHeaderForPathNames = ["Dashboard"];

  useEffect(() => {
    const fetchHeaderData = async () => {
      const location = await fetchTopLocations(host, isMobile);
      const { recentSearches = [], prefilledDataforLocation = "" } =
        fetchDataToBePrefilled();
      const { navLinks } = await fetchHeaderNavLinks();
      setNavLinks(navLinks);
      setLocation(location);
      setSearchData((prevData) => ({
        ...prevData,
        recentSearches,
        prefilledDataforLocation,
      }));
    };

    fetchHeaderData();
  }, [host, isMobile, isLoggedIn]);

  useEffect(() => {
    (async () => {
      const uniqueId = await getAbModuleId();

      resolveABVariant(
        ["New SEO_page"],
        { experimentName: "Search_Page_consolidation", id: uniqueId },
        () => {
          setShowNewSEOPage(true);
        },
        () => {
          setShowNewSEOPage(false);
        },
      );
    })();
  }, []);

  const headerProps = {
    SHOW_TOP_LOCATION,
    url: API_ENDPOINT(host).thorApi.cmsData(category).headerLinks,
    profileImgUrl: authUserData?.userProfileImage,
    altName: brandLogoName,
    linksComponentData: {
      headerData: navLinks,
      showMegaDropDown: !isMobile,
      showLinks: !isMobile,
    },
    searchComponentData: {
      ...searchData,
      expYearList: generateExperienceYear(),
      topLocations: location,
      isMobile,
      showSearchBar: !isMobile,
      showSearchBarInHeader: !isMobile,
      showSearchBarBelowHeader:
        showSearchBarBelowHeaderForPathNames.includes(props.pathName) &&
        isMobile,
      processSearchedData: ({ query, location, exp }: queryType) =>
        processSearchedData({ query, location, exp, showNewSEOPage }),
      callAutoFillAPI,
      recentSearchSelectHandler,
      getEventdata: (data: EventTrackingDataType) => {
        cleverTapAndMixPanelEvents.track("Top Navigation", {
          eventValue: data?.ctProp?.autoSuggestValue,
        });
      },
    },
    logoUrl: lottieUrl?.includes(".json")
      ? lottieUrl
      : API_ENDPOINT(host)?.media.founditLogo,
    isShowBottomNav: !isApp,
    noAvatarPages: ["Account"],
    pageName: props?.pathName,
    logoHeight: isMobile ? 16 : lottieUrl?.includes(".json") ? 34 : 24,
    isLoggedIn: isLoggedIn,
    isMobile,
    userName: authUserData?.userName || "Seeker",
    expYearList: generateExperienceYear(),
    recentSearchUrl: API_ENDPOINT(host)?.ravenApi.autoFill,
    prefillSearchForm: false,
    loginRegisterActionHandler: handlerLoginRegister(isLoggedIn),
    profileScore: authUserData?.userProfileScore,
    initTabs: mobileBottomNav(isLoggedIn),
    userIconsData: userIconsData(
      notificationCount,
      cartCount,
      cart,
      kiwiUserId,
      site_map,
    ),
    iconDataForGuest: iconDataForGuest(cartCount || 0, cart),
    accountListData: accountList(),
    companyListData: companyList(mediaUrl || null),
    cartData: cartData(cartCount || 0, cart),
    isLandingPage: true,
    isJobSaved: false,
    getEventdata: (data: EventTrackingDataType) => {
      cleverTapAndMixPanelEvents.track("Top Navigation", {
        eventValue: data?.ctProp?.categoryServices,
      });
    },
    mainLinkData: hamburgerData && hamburgerData.mainLinkData,
    secondLinkData: hamburgerData && hamburgerData.secondaryLinkData,
    quickLinks: hamburgerData && hamburgerData.quickLinkData,
    currentCompany: authUserData?.userCompanyName,
    currentDesignation: authUserData?.userDesignation,
    profileUpdatedAt: authUserData?.updatedAt,
    bannerData: hamburgerBanner(mediaUrl),
    showHeader:
      showHeaderInMobileForPathNames.includes(props?.pathName) || !isMobile,
  };

  return <Header {...headerProps} />;
};

export default memo(CustomHeader);
